import { ICONS } from "./icon"
import type { IconName } from "./icon"
import { reactive } from "vue"
import batteryDataRaw from "./battery.json"

export type BatteryState = {
  [shortName: string]: {
    statename: string;
    icon: IconName;
    description: string;
    authur: string;
    fulfilled: boolean;
    address: string;
    blockchainHash: string;
    document: DocumentItem[];
    year: {
      [year: string]: {
        month: {
          [month: string]: {
            description: string;
            verifier: string;
            lat: number;
            long: number;
            authur: string;
            date: string;
            owner: string;
            eventId: string;
          };
        };
      };
    };
  };
};

type DocumentItem = {
  Url: string;
  docName: string;
};

type CarbonFootprintStage =
  | 'RawMaterialExtraction'
  | 'MainProduction'
  | 'Distribution'
  | 'Recycling';

type CarbonFootprintValue = {
  stage: CarbonFootprintStage;
  value: number;
};

function sumCarbonFootprint(states: CarbonFootprintValue[]): number {
  return states.reduce((sum, state) => sum + state.value, 0);
}

function classifyCarbonFootprint(footprint: number): string {
  if (footprint <= 50) return 'A';
  if (footprint <= 100) return 'B';
  if (footprint <= 150) return 'C';
  if (footprint <= 200) return 'D';
  return 'E';
}

function totalCO2(parts: {
  rawMaterialCO2: number;
  mainProductionCO2: number;
  distributionCO2: number;
  recyclingCO2: number;
}) {
  return (
    (parts.rawMaterialCO2 ?? 0) +
    (parts.mainProductionCO2 ?? 0) +
    (parts.distributionCO2 ?? 0) +
    (parts.recyclingCO2 ?? 0)
  );
}

type BatteryChemistry = {
  shortName: string;
  clearName: string;
};

type BatteryMaterial = {
  location: string; // Cathode | Anode | Electrolyte
  name: string;
  casNumber: string;
  mass: number;
  isCritical: boolean;
};

type HazardousSubstance = {
  name: string;
  casNumber: string;
  concentration: number; // %
  class: 'Acute Toxicity' | 'Skin Corrosion Or Irritation' | 'Eye Damage Or Irritation';
  impact: string[];
  location: string;
};

type DismantlingDocument = {
  documentType: 'DismantlingManual' | 'Drawing' | 'BillOfMaterial' | 'Model3D' | 'RemovalManual' | 'OtherManual' | 'safety' | 'waste' | 'separate' | 'treatment' | 'wastePrevention' | 'separateCollection' | 'eolManagement';
  documentName: string;
  documentURL: string;
};

type SparePartComponent = {
  partName: string;
  partNumber: string;
};

type SupplierAddress = {
  addressCountry: string;
  postalCode: string;
  streetAddress: string;
};

type SparePartSupplier = {
  nameOfSupplier: string;
  addressOfSupplier: SupplierAddress;
  emailAddressOfSupplier: string;
  supplierWebAddress: string;
  components: SparePartComponent[];
};

type SafetyMeasures = {
  safetyInstructions: string; // URL to manual/PDF
  extinguishingAgent: string[]; // Example: ["A", "B", "C"]
};

type RecycledMaterialShare = {
  recycledMaterial: 'Nickel' | 'Cobalt' | 'Lithium' | 'Lead';
  preConsumerShare: number; // percentage (0-100)
  postConsumerShare: number; // percentage (0-100)
};

type CircularityData = {
  dismantlingDocuments: DismantlingDocument[];
  sparePartSuppliers: SparePartSupplier[];
  safetyMeasures: SafetyMeasures;
  recycledContent: RecycledMaterialShare[];
  renewableContent: number; // percentage (0-100), show even if 0
};

export type BatteryPerformanceDurability = {
  general: {
    remainingCapacityAh: number;
    remainingPowerCapabilityW: number;
    remainingRoundTripEfficiencyPercent: number;
    evolutionOfSelfDischargePercent: number;
  };
  capacityEnergyVoltage: {
    ratedCapacityAh: number;
    remainingCapacityAh: number;
    capacityFadePercent: number;
    certifiedUsableBatteryEnergyKWh: number;
    remainingUsableBatteryEnergyKWh: number;
    stateOfCertifiedEnergyPercent: number;
    stateOfChargePercent: number;
    minimumVoltageV: number;
    maximumVoltageV: number;
    nominalVoltageV: number;
  };
  powerCapability: {
    originalPowerCapabilityW: number;
    remainingPowerCapabilityW: number;
    powerFadePercent: number;
    maxPermittedPowerW: number;
    powerToEnergyRatio: number; // W/Wh
  };
  roundTripEfficiency: {
    initialEfficiencyPercent: number;
    efficiencyAtHalfCycleLifePercent: number;
    remainingEfficiencyPercent: number;
    efficiencyFadePercent: number;
  };
  selfDischarge: {
    initialSelfDischargePercentPerMonth: number;
    currentSelfDischargePercentPerMonth: number;
    evolutionOfSelfDischargePercent: number;
  };
  internalResistance: {
    initialResistanceOhm: number;
    resistanceIncreasePercent: number;
  };
  lifetime: {
    expectedYears: number;
    expectedCycleCount: number;
    fullCycleCount: number;
    referenceTest: string;
    cRate: number; // A/Ah
    energyThroughputKWh: number;
    capacityThroughputAh: number;
    capacityExhaustionThresholdPercent: number;
  };
  temperatureCondition: {
    currentTemperatureC: number;
    idleTemperatureRange: {
      minC: number;
      maxC: number;
    };
    timeAboveMaxTempMin: number;
    timeBelowMinTempMin: number;
    chargeTimeAboveMaxTempMin: number;
    chargeTimeBelowMinTempMin: number;
  };
  negativeEvents: {
    deepDischargeCount: number;
    overchargeCount: number;
    accidentReportPdfUrl?: string;
  };
};

export type BatteryStructure = {
  __id: string; //battery passport identifier 5
  usertoken: string; // Unique identifier for the user
  currentStage: number;
  battId: string; //battery identifier 6
  rfidCode: string;
  nfcTag: string;
  battModel: string; //battery model 7
  operatorId: string;
  battCategory: string;
  batteryMass: number;
  batteryStatus: string; //"Original" "Repurposed" "Reused" "Remanufactured" "Waste"
  puttingIntoService: Date;
  warrentyPeriod: number;
  operatorState: string;
  operatorTrademark: string;
  operatorPostal: number;
  operatorWeb: string;
  operatorEmail: string;
  originalVIN: string;
  batterySerial: string;

  manufacturingName: string;
  manufacturingPlace: string;
  facilityIdentifier: string;
  manufacturingDate: Date;
  manufacturingInformation: string;

  //CarbonFootprint
  batteryCarbonFootprint: number;
  rawMaterialCO2: number,
  mainProductionCO2: number,
  distributionCO2: number,
  recyclingCO2: number,
  carbonFootprintPerformanceClass: string;
  AbsoluteCarbonFootprint: number;

  //supplychain
  supplyChainDueDiligenceReport: string;
  thirdPartyAussurances: string;
  SupplyChainIndicies: number;

  chemistry: BatteryChemistry;
  materials: BatteryMaterial[];
  hazardousSubstances: HazardousSubstance[];

  dismantlingDocuments: DismantlingDocument[];
  sparePartSuppliers: SparePartSupplier[];
  safetyMeasures: SafetyMeasures;
  recycledContent: RecycledMaterialShare[];
  renewableContent: number; // % of renewable material content
  renewableMaterials?: { name: string; shareByWeight: number }[];

  performanceDurability: BatteryPerformanceDurability;

  lat: number;
  long: number;
  state: BatteryState;
};

export const batteryStore: BatteryStructure[] = (batteryDataRaw as any[]).map(b => ({
  ...b,
  puttingIntoService: new Date(b.puttingIntoService),
  manufacturingDate: new Date(b.manufacturingDate),
})) as BatteryStructure[];

const initialBattery: BatteryStructure =
  (typeof structuredClone === 'function'
    ? structuredClone(batteryStore[0])
    : { ...batteryStore[0] }) as BatteryStructure

export const batteryHistory: BatteryStructure = reactive(initialBattery) as BatteryStructure
const STORAGE_KEY = 'battery.__id';

function assignInPlace<T extends Record<string, any>>(to: T, from: T) {
  // delete keys not present in `from`
  for (const k of Object.keys(to)) {
    if (!(k in from)) delete (to as any)[k];
  }
  // copy/overwrite keys from `from`
  for (const [k, v] of Object.entries(from)) {
    (to as any)[k] = v;
  }
  return to;
}

export function normalizeBatteryId(s: string): string {
  return String(s ?? '')
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width
    .replace(/[–—−]/g, '-')                // en/em/minus dash -> '-'
    .trim();
}

export function findBatteryById(
  id: string,
  opts: { allowBattId?: boolean } = {}
): BatteryStructure | undefined {
  const needle = normalizeBatteryId(id).toLowerCase();
  console.log('[Debug] findBatteryById searching for:', needle, 'allowBattId:', opts.allowBattId);

  if (!needle) return undefined;

  const byId = batteryStore.find(b => b.__id?.toLowerCase() === needle);
  if (byId) {
    console.log('[Debug] Found by __id:', byId.__id);
    return byId;
  }

  if (opts.allowBattId) {
    const byBatt = batteryStore.find(b => (b as any).battId?.toLowerCase() === needle);
    if (byBatt) {
      console.log('[Debug] Found by battId:', byBatt.battId);
      return byBatt;
    }

    const byRfid = batteryStore.find(b => (b as any).rfidCode?.toLowerCase() === needle);
    if (byRfid) {
      console.log('[Debug] Found by rfidCode:', (byRfid as any).rfidCode);
      return byRfid;
    }
  }

  console.warn('[Debug] Battery not found in store. Store length:', batteryStore.length);
  return undefined;
}

export function setBatteryById(
  id: string,
  opts: { persist?: boolean; allowBattId?: boolean } = { persist: true, allowBattId: false }
): BatteryStructure | undefined {
  const hit = findBatteryById(id, { allowBattId: opts.allowBattId });
  if (!hit) return undefined;

  assignInPlace(batteryHistory as any, hit as any);

  if (opts.persist && typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, hit.__id);
    } catch { /* ignore */ }
  }
  return batteryHistory;
}

export function initBatteryFromStorage(): BatteryStructure {
  let initialized = false;

  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const hit = findBatteryById(saved);
      if (hit) {
        assignInPlace(batteryHistory as any, hit as any);
        initialized = true;
      }
    }
  }

  if (!initialized && batteryStore.length) {
    assignInPlace(batteryHistory as any, batteryStore[0] as any);
  }
  return batteryHistory;
}